import { useState, useEffect } from 'react';
import { dashboard, notifications, aiPlanning, taskNotifications } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  CheckCircle2, Clock, AlertTriangle, ArrowRight, Calendar,
  DollarSign, TrendingUp, Zap, Bell, ChevronRight, Target,
  ListTodo, FileText, Users, Play, Sparkles, Brain, RefreshCw, Mail, Send
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

function FocusTaskCard({ task, type = "task" }) {
  const priorityColors = {
    high: 'border-l-rose-500 bg-rose-50',
    medium: 'border-l-amber-500 bg-amber-50',
    low: 'border-l-slate-400 bg-slate-50',
  };
  
  const isOverdue = task.days_overdue > 0;
  
  return (
    <div className={`p-3 rounded-lg border-l-4 ${priorityColors[task.priority] || priorityColors.medium} hover:shadow-sm transition-all`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium truncate">{task.title}</p>
          <div className="flex items-center gap-2 mt-1">
            {isOverdue && (
              <Badge variant="destructive" className="text-xs">
                {task.days_overdue} día(s) vencida
              </Badge>
            )}
            {task.due_date && !isOverdue && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {formatDate(task.due_date)}
              </span>
            )}
            {task.assigned_name && (
              <span className="text-xs text-muted-foreground">
                → {task.assigned_name}
              </span>
            )}
          </div>
        </div>
        <Badge variant="outline" className={`text-xs ${
          task.priority === 'high' ? 'border-rose-300 text-rose-700' :
          task.priority === 'medium' ? 'border-amber-300 text-amber-700' : 
          'border-slate-300 text-slate-600'
        }`}>
          {task.priority === 'high' ? 'Alta' : task.priority === 'medium' ? 'Media' : 'Baja'}
        </Badge>
      </div>
    </div>
  );
}

function AlertCard({ alert }) {
  const icons = {
    'alert-triangle': AlertTriangle,
    'dollar-sign': DollarSign,
    'user-plus': Users,
  };
  const Icon = icons[alert.icon] || Bell;
  
  const colors = {
    danger: 'bg-rose-50 border-rose-200 text-rose-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
  };
  
  return (
    <Link to={`/${alert.action}`}>
      <div className={`p-3 rounded-lg border ${colors[alert.type]} flex items-center gap-3 hover:shadow-sm transition-all cursor-pointer`}>
        <Icon className="h-5 w-5 flex-shrink-0" />
        <p className="text-sm font-medium flex-1">{alert.message}</p>
        <ChevronRight className="h-4 w-4 flex-shrink-0 opacity-50" />
      </div>
    </Link>
  );
}

function KPIMiniCard({ title, value, subtitle, icon: Icon, trend, color = "blue" }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    rose: 'bg-rose-50 text-rose-600',
  };
  
  return (
    <Card className="hover:shadow-md transition-all">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <p className="text-2xl font-bold">{value}</p>
            <p className="text-xs text-muted-foreground">{title}</p>
          </div>
          {trend && (
            <Badge variant="outline" className={trend > 0 ? 'text-emerald-600' : 'text-rose-600'}>
              {trend > 0 ? '+' : ''}{trend}%
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ProductivityMeter({ score }) {
  const getScoreColor = (s) => {
    if (s >= 80) return 'text-emerald-500';
    if (s >= 50) return 'text-amber-500';
    return 'text-rose-500';
  };
  
  const getScoreLabel = (s) => {
    if (s >= 80) return '¡Excelente!';
    if (s >= 50) return 'Bien';
    return 'Necesita atención';
  };
  
  return (
    <div className="text-center">
      <div className="relative inline-flex items-center justify-center">
        <svg className="w-32 h-32 transform -rotate-90">
          <circle
            cx="64"
            cy="64"
            r="56"
            stroke="currentColor"
            strokeWidth="8"
            fill="none"
            className="text-gray-200"
          />
          <circle
            cx="64"
            cy="64"
            r="56"
            stroke="currentColor"
            strokeWidth="8"
            fill="none"
            strokeDasharray={`${score * 3.52} 352`}
            className={getScoreColor(score)}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute flex flex-col items-center">
          <span className={`text-3xl font-bold ${getScoreColor(score)}`}>{score}</span>
          <span className="text-xs text-muted-foreground">puntos</span>
        </div>
      </div>
      <p className={`mt-2 font-medium ${getScoreColor(score)}`}>{getScoreLabel(score)}</p>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [todayData, setTodayData] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [taskAlerts, setTaskAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingAi, setLoadingAi] = useState(false);
  const [autoPrioritizing, setAutoPrioritizing] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, todayRes, aiRes, alertsRes] = await Promise.all([
        dashboard.getStats(),
        dashboard.getToday(),
        aiPlanning.getTaskSuggestions().catch(() => ({ data: null })),
        taskNotifications.getAlerts(3).catch(() => ({ data: null }))
      ]);
      setStats(statsRes.data);
      setTodayData(todayRes.data);
      setAiSuggestions(aiRes.data);
      setTaskAlerts(alertsRes.data);
    } catch (err) {
      console.error('Error loading dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshAiSuggestions = async () => {
    setLoadingAi(true);
    try {
      const res = await aiPlanning.getTaskSuggestions();
      setAiSuggestions(res.data);
      toast.success('Sugerencias actualizadas');
    } catch (err) {
      toast.error('Error al actualizar sugerencias');
    } finally {
      setLoadingAi(false);
    }
  };

  const handleAutoPrioritize = async () => {
    setAutoPrioritizing(true);
    try {
      const res = await aiPlanning.autoPrioritize();
      toast.success(res.data.message);
      loadData();
    } catch (err) {
      toast.error('Error al auto-priorizar tareas');
    } finally {
      setAutoPrioritizing(false);
    }
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const res = await taskNotifications.sendReminders(3);
      if (res.data.success) {
        toast.success(`${res.data.notifications_sent} recordatorios enviados`);
        if (res.data.errors?.length > 0) {
          toast.warning(`${res.data.errors.length} errores encontrados`);
        }
      }
    } catch (err) {
      toast.error('Error al enviar recordatorios');
    } finally {
      setSendingReminders(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const greeting = todayData?.greeting || 'Hola';
  const focus = todayData?.focus || {};
  const alerts = todayData?.alerts || [];
  const summary = todayData?.summary || {};
  const productivityScore = todayData?.productivity_score || 75;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="dashboard">
      {/* Header with Greeting */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading tracking-tight">
            {greeting} 👋
          </h1>
          <p className="text-muted-foreground">
            Aquí está tu resumen de hoy
          </p>
        </div>
        <Link to="/calendar">
          <Button variant="outline" className="gap-2">
            <Calendar className="h-4 w-4" />
            Ver Calendario
          </Button>
        </Link>
      </div>

      {/* Alerts Section */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((alert, idx) => (
            <AlertCard key={idx} alert={alert} />
          ))}
        </div>
      )}

      {/* Main Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column - Focus Tasks */}
        <div className="lg:col-span-2 space-y-6">
          {/* Overdue Tasks */}
          {focus.overdue?.length > 0 && (
            <Card className="border-rose-200">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2 text-rose-700">
                    <AlertTriangle className="h-5 w-5" />
                    Tareas Vencidas
                  </CardTitle>
                  <Link to="/tasks">
                    <Button variant="ghost" size="sm" className="gap-1 text-rose-600">
                      Ver todas <ArrowRight className="h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {focus.overdue.map((task) => (
                  <FocusTaskCard key={task.task_id} task={task} />
                ))}
              </CardContent>
            </Card>
          )}

          {/* Today's Tasks */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  Para Hoy
                </CardTitle>
                <Link to="/tasks">
                  <Button variant="ghost" size="sm" className="gap-1">
                    Ver todas <ArrowRight className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {focus.today?.length > 0 ? (
                <div className="space-y-2">
                  {focus.today.map((task) => (
                    <FocusTaskCard key={task.task_id} task={task} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-muted-foreground">
                  <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
                  <p>¡No tienes tareas para hoy!</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Tasks */}
          {focus.upcoming?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="h-5 w-5 text-amber-500" />
                    Próximas (7 días)
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {focus.upcoming.slice(0, 3).map((task) => (
                  <FocusTaskCard key={task.task_id} task={task} />
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Stats & Productivity */}
        <div className="space-y-6">
          {/* Productivity Score */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Zap className="h-5 w-5 text-amber-500" />
                Productividad
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ProductivityMeter score={productivityScore} />
              <div className="mt-4 grid grid-cols-2 gap-2 text-center text-sm">
                <div className="p-2 bg-rose-50 rounded-lg">
                  <p className="font-bold text-rose-600">{summary.overdue_tasks || 0}</p>
                  <p className="text-xs text-muted-foreground">Vencidas</p>
                </div>
                <div className="p-2 bg-amber-50 rounded-lg">
                  <p className="font-bold text-amber-600">{summary.pending_tasks || 0}</p>
                  <p className="text-xs text-muted-foreground">Pendientes</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          {stats && (
            <>
              <KPIMiniCard
                title="Tareas Completadas"
                value={`${stats.tasks.completion_rate}%`}
                icon={CheckCircle2}
                color="green"
              />
              <KPIMiniCard
                title="Horas Esta Semana"
                value={`${stats.time.hours_this_week}h`}
                icon={Clock}
                color="blue"
              />
              <KPIMiniCard
                title="Por Cobrar"
                value={formatCurrency(stats.invoices.total_pending)}
                icon={DollarSign}
                color={stats.invoices.overdue_count > 0 ? 'rose' : 'amber'}
              />
            </>
          )}

          {/* Quick Actions */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Acciones Rápidas</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-2">
              <Link to="/tasks">
                <Button variant="outline" className="w-full h-auto py-3 flex-col gap-1">
                  <ListTodo className="h-5 w-5" />
                  <span className="text-xs">Nueva Tarea</span>
                </Button>
              </Link>
              <Link to="/invoices">
                <Button variant="outline" className="w-full h-auto py-3 flex-col gap-1">
                  <FileText className="h-5 w-5" />
                  <span className="text-xs">Nueva Factura</span>
                </Button>
              </Link>
              <Link to="/meetings">
                <Button variant="outline" className="w-full h-auto py-3 flex-col gap-1">
                  <Play className="h-5 w-5" />
                  <span className="text-xs">Transcribir</span>
                </Button>
              </Link>
              <Link to="/crm">
                <Button variant="outline" className="w-full h-auto py-3 flex-col gap-1">
                  <Users className="h-5 w-5" />
                  <span className="text-xs">Nuevo Cliente</span>
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* AI Task Planning Section */}
      {aiSuggestions && (
        <Card className="bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-violet-600" />
                Planificación Inteligente
              </CardTitle>
              <div className="flex gap-2 flex-wrap">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={refreshAiSuggestions}
                  disabled={loadingAi}
                  className="gap-1"
                >
                  <RefreshCw className={`h-4 w-4 ${loadingAi ? 'animate-spin' : ''}`} />
                  Actualizar
                </Button>
                {isAdmin && (
                  <>
                    <Button 
                      variant="default" 
                      size="sm" 
                      onClick={handleAutoPrioritize}
                      disabled={autoPrioritizing}
                      className="gap-1 bg-violet-600 hover:bg-violet-700"
                    >
                      <Brain className={`h-4 w-4 ${autoPrioritizing ? 'animate-pulse' : ''}`} />
                      Auto-Priorizar
                    </Button>
                    <Button 
                      variant="default" 
                      size="sm" 
                      onClick={handleSendReminders}
                      disabled={sendingReminders}
                      className="gap-1 bg-emerald-600 hover:bg-emerald-700"
                      data-testid="send-reminders-btn"
                    >
                      <Mail className={`h-4 w-4 ${sendingReminders ? 'animate-pulse' : ''}`} />
                      {sendingReminders ? 'Enviando...' : 'Enviar Recordatorios'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              {/* AI Insights */}
              {aiSuggestions.insights?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-violet-800 flex items-center gap-1">
                    <Brain className="h-4 w-4" />
                    Insights
                  </p>
                  {aiSuggestions.insights.map((insight, idx) => (
                    <div 
                      key={idx} 
                      className={`p-2 rounded-lg text-sm ${
                        insight.type === 'warning' ? 'bg-amber-100 text-amber-800' :
                        insight.type === 'success' ? 'bg-emerald-100 text-emerald-800' :
                        'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {insight.message}
                    </div>
                  ))}
                </div>
              )}

              {/* Focus Today with AI Reasons */}
              {aiSuggestions.focus_today?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-violet-800 flex items-center gap-1">
                    <Target className="h-4 w-4" />
                    Enfoque Recomendado
                  </p>
                  {aiSuggestions.focus_today.slice(0, 3).map((task) => (
                    <div key={task.task_id} className="p-2 bg-white rounded-lg border border-violet-200">
                      <p className="font-medium text-sm truncate">{task.title}</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {task.ai_reasons?.slice(0, 2).map((reason, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs bg-violet-50">
                            {reason}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Workload Summary */}
              {aiSuggestions.workload && (
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-violet-800 flex items-center gap-1">
                    <TrendingUp className="h-4 w-4" />
                    Tu Carga de Trabajo
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2 bg-white rounded-lg border border-violet-200 text-center">
                      <p className="text-2xl font-bold text-violet-600">{aiSuggestions.workload.your_tasks}</p>
                      <p className="text-xs text-muted-foreground">Tus Tareas</p>
                    </div>
                    <div className="p-2 bg-white rounded-lg border border-violet-200 text-center">
                      <p className="text-2xl font-bold text-violet-600">{aiSuggestions.workload.estimated_hours}h</p>
                      <p className="text-xs text-muted-foreground">Horas Est.</p>
                    </div>
                    <div className="p-2 bg-white rounded-lg border border-rose-200 text-center col-span-2">
                      <p className="text-lg font-bold text-rose-600">{aiSuggestions.workload.overdue_count}</p>
                      <p className="text-xs text-muted-foreground">Tareas Vencidas</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Schedule Suggestion */}
            {aiSuggestions.schedule_suggestion?.length > 0 && (
              <div className="mt-4 p-3 bg-white rounded-lg border border-violet-200">
                <p className="text-sm font-semibold text-violet-800 mb-2 flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  Agenda Sugerida para Hoy
                </p>
                <div className="flex flex-wrap gap-2">
                  {aiSuggestions.schedule_suggestion.map((item, idx) => (
                    <Badge 
                      key={idx} 
                      variant="outline" 
                      className={`${
                        item.priority === 'high' ? 'border-rose-300 bg-rose-50' :
                        item.priority === 'medium' ? 'border-amber-300 bg-amber-50' :
                        'border-slate-300 bg-slate-50'
                      }`}
                    >
                      {item.title} ({item.suggested_duration}h)
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
